import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LabeledTextField } from "@/core/components/LabeledTextField";
import { LabeledSelect } from "@/core/components/LabeledSelect";
import { LabeledDatePicker } from "@/core/components/LabeledDatePicker";
import { ReadOnlyField } from "@/core/components/ReadOnlyField";
import { ConfirmDialog } from "@/core/components/ConfirmDialog";
import { DebouncedSearchBar } from "@/core/components/DebouncedSearchBar";
import { useToastStore } from "@/core/stores/useToastStore";
import { useThemeStore } from "@/core/stores/useThemeStore";

// Sandbox-only form values used to exercise the form primitives.
// Production routes will wrap their own `LocalizationProvider` in `App.tsx`
// once the date-picker becomes a global concern.
interface SandboxFormValues {
  textField: string;
  textFieldReadOnly: string;
  textFieldWithIcon: string;
  selectField: string;
  dateField: Date | null;
}

const SELECT_OPTIONS = [
  { value: "one", label: "Option one" },
  { value: "two", label: "Option two" },
  { value: "three", label: "Option three" },
] as const;

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h5">{title}</Typography>
        {children}
      </Stack>
    </Paper>
  );
}

export function SandboxScreen(): React.ReactElement {
  const { control } = useForm<SandboxFormValues>({
    defaultValues: {
      textField: "",
      textFieldReadOnly: "Read-only value",
      textFieldWithIcon: "",
      selectField: "",
      dateField: null,
    },
  });

  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const handleConfirm = (): void => {
    setConfirmOpen(false);
    useToastStore.getState().show({
      message: "Confirmed",
      severity: "success",
    });
  };

  const handleSearch = (query: string): void => {
    console.log("Sandbox search:", query);
  };

  const handleToast = (severity: "success" | "error" | "info" | "warning"): void => {
    useToastStore.getState().show({
      message: `${severity[0]?.toUpperCase() ?? ""}${severity.slice(1)} toast`,
      severity,
    });
  };

  const handleThemeToggle = (): void => {
    useThemeStore.getState().toggle();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Typography variant="h3">Sandbox</Typography>

          <Section title="Buttons">
            <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap" }}>
              <Button variant="contained" color="primary">
                Contained primary
              </Button>
              <Button variant="outlined" color="secondary">
                Outlined secondary
              </Button>
              <Button variant="text" color="error">
                Text error
              </Button>
            </Stack>
          </Section>

          <Section title="LabeledTextField">
            <Stack spacing={2}>
              <LabeledTextField<SandboxFormValues>
                name="textField"
                control={control}
                label="Required text"
                placeholder="Type something"
                required
              />
              <LabeledTextField<SandboxFormValues>
                name="textFieldReadOnly"
                control={control}
                label="Read-only text"
                slotProps={{ input: { readOnly: true } }}
              />
              <LabeledTextField<SandboxFormValues>
                name="textFieldWithIcon"
                control={control}
                label="Email"
                placeholder="you@example.com"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Stack>
          </Section>

          <Section title="LabeledSelect">
            <LabeledSelect<SandboxFormValues>
              name="selectField"
              control={control}
              label="Pick an option"
              options={SELECT_OPTIONS}
            />
          </Section>

          <Section title="LabeledDatePicker">
            <LabeledDatePicker<SandboxFormValues>
              name="dateField"
              control={control}
              label="Pick a date"
            />
          </Section>

          <Section title="ReadOnlyField">
            <Stack spacing={2}>
              <ReadOnlyField label="With value" value="Sourdough loaf" />
              <ReadOnlyField label="Empty value" value="" />
            </Stack>
          </Section>

          <Section title="ConfirmDialog">
            <Box>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setConfirmOpen(true);
                }}
              >
                Open confirm dialog
              </Button>
            </Box>
            <ConfirmDialog
              open={confirmOpen}
              onClose={() => {
                setConfirmOpen(false);
              }}
              onConfirm={handleConfirm}
              title="Delete item?"
              description="This action cannot be undone."
              confirmLabel="Delete"
              confirmColor="error"
            />
          </Section>

          <Section title="DebouncedSearchBar">
            <DebouncedSearchBar
              placeholder="Search recipes"
              onSearch={handleSearch}
            />
          </Section>

          <Section title="Toasts">
            <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  handleToast("success");
                }}
              >
                Success
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  handleToast("error");
                }}
              >
                Error
              </Button>
              <Button
                variant="contained"
                color="info"
                onClick={() => {
                  handleToast("info");
                }}
              >
                Info
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  handleToast("warning");
                }}
              >
                Warning
              </Button>
            </Stack>
          </Section>

          <Section title="Theme toggle">
            <Box>
              <Button variant="outlined" onClick={handleThemeToggle}>
                Toggle theme
              </Button>
            </Box>
          </Section>
        </Stack>
      </Container>
    </LocalizationProvider>
  );
}
